import React, { useState, useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  Legend,
  ComposedChart,
  Line,
} from "recharts";
import {
  Zap,
  TrendingDown,
  AlertTriangle,
  Droplets,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Filter,
  Download,
  HousePlug,
  Map,
  MousePointer2,
  ChevronRight,
  CircleAlert,
  Thermometer,
  Settings,
  Calendar,
  HardDrive,
  Lightbulb,
  ShieldCheck,
  ArrowDownCircle,
  ArrowUpCircle,
  ListFilter,
  WalletCards,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AssetReportMap from "../assetReports/AssetReportMap";
import { useNavigate } from "react-router-dom";

// --- Configuration & Updated Data ---
const ENERGY_RECORDS = [
  {
    asset: "PUMP-02",
    location: "Narwal plain 2",
    siteType: "Tubewell",
    flow: "50,705",
    head: "37.97",
    requiredKw: "39.46",
    actualKw: "76.68",
    extraKwHr: "37.22",
    extraKwYear: "2,71,737.11",
    annualLoss: "1902159.791", // Kept as number for calculation
    waste: "48.5%",
    status: "Critical",
  },
  {
    asset: "PUMP-01",
    location: "Narwal plain 1",
    siteType: "Tubewell",
    flow: "48,301",
    head: "58.51",
    requiredKw: "57.92",
    actualKw: "88.95",
    extraKwHr: "31.03",
    extraKwYear: "2,26,534.05",
    annualLoss: "1585738.37", // Kept as number
    waste: "34.9%",
    status: "Warning",
  },
];

// Data for the Heat Map
const DUMMY_ASSETS = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  name: `Pump-${100 + i}`,
  lat: 32.7266 + (Math.random() - 0.5) * 0.1,
  lng: 74.857 + (Math.random() - 0.5) * 0.1,
  status: i % 3 === 0 ? "Critical" : i % 3 === 1 ? "Warning" : "Optimal",
}));

const statusColors = {
  Critical: "#ef4444",
  Warning: "#f59e0b",
  Optimal: "#22c55e",
  Default: "#3b82f6",
};

const EnergyEfficiency = () => {
  const themeMode = useSelector((state) => state.theme?.mode);
  const textColor = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderCol = themeMode ? "border_them_2" : "border-primary";
  const cardBg = themeMode ? "bg_light_main" : "bg-white";
  const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";
const navigate = useNavigate();
  // --- FILTER STATES ---
  const [siteTypeFilter, setSiteTypeFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [headRangeFilter, setHeadRangeFilter] = useState("all");

  // --- FILTER LOGIC ---
  const filteredRecords = useMemo(() => {
    return ENERGY_RECORDS.filter((item) => {
      const siteMatch =
        siteTypeFilter === "all" ||
        item.siteType.toLowerCase() === siteTypeFilter.toLowerCase();
      const locMatch =
        locationFilter === "all" ||
        item.location.toLowerCase() === locationFilter.toLowerCase();

      let headMatch = true;
      const headVal = parseFloat(item.head);
      if (headRangeFilter === "low") headMatch = headVal < 40;
      else if (headRangeFilter === "high") headMatch = headVal >= 40;

      return siteMatch && locMatch && headMatch;
    });
  }, [siteTypeFilter, locationFilter, headRangeFilter]);

  // --- DYNAMIC KPI CALCULATION ---
  const dynamicStats = useMemo(() => {
    const totalExtra = filteredRecords.reduce(
      (acc, curr) => acc + parseFloat(curr.extraKwYear.replace(/,/g, "")),
      0
    );
    const totalLoss = filteredRecords.reduce(
      (acc, curr) => acc + parseFloat(curr.annualLoss),
      0
    );

    return {
      totalExtraKwYear: totalExtra.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      totalAnnualLoss: `₹ ${totalLoss.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
    };
  }, [filteredRecords]);

  return (
    <div className={`w-full ${bgColorClass} h-full ${textColor}`}>
      {/* Header */}
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderCol} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColor} items-center gap-x-2`}
        >
          <HousePlug size={16} />
          <p className="font-semibold tracking-tight">
            Energy Efficiency
          </p>
        </div>
        <div className="flex items-center gap-4">
         
          {/* <Button size="icon" variant="ghost" className="h-8 w-8 opacity-50">
            <Download size={16} />
          </Button> */}
        </div>
      </div>

      <div className="h-[calc(100vh-57px)] pb-10 sidebar_scroll overflow-y-auto">
        <div className="py-3 md:px-5 lg:px-12 px-6 space-y-4">
          <div
            className={`${cardBg}  p-2 rounded-2xl flex flex-wrap gap-4 items-center`}
          >
            <div
              className={`flex items-center gap-2 ${borderCol} mr-2`}
            >
              <ListFilter size={16} />
              <span className="text-[10px] font-black uppercase tracking-widest">
                Filters
              </span>
            </div>

            <Select
              onValueChange={(v) => setSiteTypeFilter(v)}
              defaultValue="all"
            >
              <SelectTrigger
                className={`w-[130px] h-8 bg-transparent ${borderCol} text-xs`}
              >
                <SelectValue placeholder="Site Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                <SelectItem value="tubewell">Tubewell</SelectItem>
                <SelectItem value="wtp">WTP</SelectItem>
                <SelectItem value="pumping-station">Pumping Station</SelectItem>
              </SelectContent>
            </Select>

            <Select
              onValueChange={(v) => setLocationFilter(v)}
              defaultValue="all"
            >
              <SelectTrigger
                className={`w-[180px] h-8 bg-transparent ${borderCol} text-xs`}
              >
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="narwal plain 1">Narwal plain 1</SelectItem>
                <SelectItem value="narwal plain 2">Narwal plain 2</SelectItem>
              </SelectContent>
            </Select>

            <Select
              onValueChange={(v) => setHeadRangeFilter(v)}
              defaultValue="all"
            >
              <SelectTrigger
                className={`w-[120px] h-8 bg-transparent ${borderCol} text-xs`}
              >
                <SelectValue placeholder="Head Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Head</SelectItem>
                <SelectItem value="low">Low Head (&lt;40)</SelectItem>
                <SelectItem value="high">High Head (&gt;40)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className={`${cardBg} border ${borderCol} p-8 rounded-2xl flex justify-between items-center bg-red-500/5 relative overflow-hidden group`}
            >
              <div className="relative z-10">
                <p className="text-[10px] font-black uppercase tracking-widest mb-1 opacity-60">
                  Total Extra kW / Year
                </p>
                <h2 className="text-2xl font-black text-red-500 tracking-tighter">
                  {dynamicStats.totalExtraKwYear}
                </h2>
              </div>
              <Activity
                size={80} 
                className="text-red-500 opacity-5 absolute -right-4 -bottom-4" 
              />
            </div>
            <div
              className={`${cardBg} border ${borderCol} p-8 rounded-2xl flex justify-between items-center bg-yellow-500/5 relative overflow-hidden group`}
            >
              <div className="relative z-10">
                <p className="text-[10px] font-black uppercase tracking-widest mb-1 opacity-60">
                  Total Annual Loss
                </p>
                <h2 className="text-2xl font-black text-yellow-500 tracking-tighter">
                  {dynamicStats.totalAnnualLoss}
                </h2>
              </div>
              <TrendingDown
                size={80}
                className="text-yellow-500 opacity-5 absolute -right-4 -bottom-4"
              />
            </div>
          </div>

          {/* --- Main Table: Energy Audit Report --- */}
          <Card
            className={`${cardBg} shadow-none border-none ${textColor} overflow-hidden`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-semibold">
                  Energy Efficiency - Detailed Report
                </h3>
                <p className="text-xs opacity-40">
                  Required vs Actual consumption analysis
                </p>
              </div>
            </div>
            <div className={`overflow-x-auto border rounded-2xl ${borderCol}`}>
              <table className="w-full text-left">
                <thead
                  className={`${tableHedBg} text-[9px] uppercase font-black tracking-widest`}
                >
                  <tr>
                    <th className="p-5">Asset Detail</th>
                    <th className="p-5 text-center">Required kW</th>
                    <th className="p-5 text-center">Actual kW</th>
                    <th className="p-5 text-center">Extra kW/Year</th>
                    <th className="p-5 text-center text-yellow-500">
                      Annual Loss
                    </th>
                    <th className="p-5 text-right">Operational Status</th>
                  </tr>
                </thead>
                <tbody className="text-[11px] font-bold">
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-t ${borderCol} hover:bg-blue-500/5 transition-colors`}
                      >
                        <td className="p-5">
                          <p className="text-xs font-black tracking-tight">
                            {row.asset}
                          </p>
                          <p className="text-[9px] opacity-40 uppercase">
                            {row.location} | {row.siteType}
                          </p>
                        </td>
                        <td className="p-5 text-center text-green-500">
                          <div className="flex items-center justify-center gap-1.5">
                            <ArrowDownCircle size={14} /> {row.requiredKw}
                          </div>
                        </td>
                        <td className="p-5 text-center text-red-500">
                          <div className="flex items-center justify-center gap-1.5">
                            <ArrowUpCircle size={14} /> {row.actualKw}
                          </div>
                        </td>
                        <td className="p-5 text-center text-red-500 font-mono tracking-tighter italic">
                          {row.extraKwYear}
                        </td>
                        <td className="p-5 text-center text-yellow-500 text-xs font-black tracking-tight">
                          ₹{" "}
                          {parseFloat(row.annualLoss).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-5 text-right">
                          <Badge
                            className={
                              row.status === "Critical"
                                ? "bg-red-500 text-white font-black"
                                : "bg-orange-500 text-white font-black"
                            }
                          >
                            {row.waste} WASTE
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-10 text-center opacity-50 italic"
                      >
                        No assets match the selected audit filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Heat Map Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div
              className={`lg:col-span-8 ${cardBg} border ${borderCol} rounded-[2rem]`}
            >
              <div className="flex justify-between items-center p-4">
                <h3 className="text-base font-sm flex items-center gap-2 tracking-widest">
                  <Map size={18} className="text-blue-500" /> Asset Health Heat
                  Map
                </h3>
              </div>
              <AssetReportMap
                statusColors={statusColors}
                DUMMY_ASSETS={DUMMY_ASSETS}
                mapHeight="500px"
                statusFilter="All"
              />
            </div>

            <div className="lg:col-span-4 space-y-6">
              <div
                className={`${cardBg} border ${borderCol} rounded-[2rem] p-3`}
              >
                <h3 className="font-base font-semibold tracking-[0.2em] mb-6 flex items-center gap-2 opacity-50">
                  <WalletCards size={16} /> High
                  Maintenance
                </h3>
                <div className="space-y-4">
                  <div className="p-3 rounded-2xl bg-slate-500/5 border border-slate-500/10 flex justify-between items-center">
                    <div>
                      <p className="text-xs font-black tracking-tight">
                        Pump-02 (Narwal 2)
                      </p>
                      <p className="text-[9px] font-bold text-red-500 uppercase mt-0.5">
                        14 Failures/yr
                      </p>
                    </div>
                    <p className="text-[10px] font-black text-slate-400">
                      ₹4.2k
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-600 rounded-[2rem] p-8 text-white relative overflow-hidden group">
                <div className="relative z-10">
                  <h4 className="font-black text-xs uppercase tracking-widest mb-4 opacity-70 italic">
                    Case Study: Intelligence Insight
                  </h4>
                  <p className="text-sm font-medium leading-relaxed italic mb-6">
                    "Overall system efficiency is 12% below baseline in Narwal
                    plain 2. Scheduling an inspection to prevent critical
                    bearing seizure."
                  </p>
                  <Button onClick={()=> navigate(`/blocks/40f175d4-12aa-44f7-9a1c-4d0115db0499/records`)} className="w-full bg-white text-blue-600 font-black text-[10px] uppercase tracking-[0.2em] py-5 rounded-2xl hover:bg-slate-100 transition-all active:scale-95 group">
                    Schedule Inspection{" "}
                    <ChevronRight
                      size={14}
                      className="ml-1 group-hover:translate-x-1 transition-transform"
                    />
                  </Button>
                </div>
                <MousePointer2 className="absolute -right-4 -bottom-4 size-24 text-white/5 rotate-12" />
              </div>
            </div>
          </div>
        </div>
      </div>

     
    </div>
  );
};

export default EnergyEfficiency;
